import React from 'react'
import {
    BulkEnrollmentSelectedAddOn,
    BulkEnrollmentSelectedPackage,
    clampBulkEnrollmentPackageQuantity,
    formatBulkEnrollmentAmount,
    formatBulkEnrollmentDiscount,
    getBulkEnrollmentDiscountAmount,
    getBulkEnrollmentRowThumbnails,
    getBulkEnrollmentRowTotal,
    getExistingProductQuantity,
    setBulkEnrollmentAddOnQuantity
} from 'helpers/bulkEnrollmentHelpers'
import { BulkEnrollmentAddOnGrid } from './BulkEnrollmentAddOnGrid'
import { BulkEnrollmentQuantityStepper } from './BulkEnrollmentQuantityStepper'
import { ClientType } from 'storeTypes'

type BulkEnrollmentClientPackagesProps = {
    selectedPackage: BulkEnrollmentSelectedPackage
    quantity: string
    selectedAddOns: BulkEnrollmentSelectedAddOn[]
    client?: ClientType | null
    readOnly?: boolean
    onQuantityChange: (quantity: string) => void
    onAddOnsChange: (selectedAddOns: BulkEnrollmentSelectedAddOn[]) => void
}

export const BulkEnrollmentThumbnails = ({
    selectedPackage,
    selectedAddOns
}: {
    selectedPackage: BulkEnrollmentSelectedPackage
    selectedAddOns: BulkEnrollmentSelectedAddOn[]
}) => (
    <div className="bulk-enrollment-thumbnails">
        {getBulkEnrollmentRowThumbnails(selectedPackage, selectedAddOns).map(
            (thumbnail) =>
                thumbnail.image_url ? (
                    <img
                        key={thumbnail.key}
                        src={thumbnail.image_url}
                        alt={thumbnail.name}
                        className="bulk-enrollment-thumbnails__image"
                    />
                ) : (
                    <div
                        key={thumbnail.key}
                        className="bulk-enrollment-thumbnails__image bulk-enrollment-thumbnails__image--empty"
                        aria-hidden="true"
                    />
                )
        )}
    </div>
)

export const BulkEnrollmentClientPackages = ({
    selectedPackage,
    quantity,
    selectedAddOns,
    client,
    readOnly = false,
    onQuantityChange,
    onAddOnsChange
}: BulkEnrollmentClientPackagesProps) => {
    const packageQuantity = clampBulkEnrollmentPackageQuantity(Number(quantity))
    const addOns = selectedPackage.addOns || []
    const existingQuantity = getExistingProductQuantity(
        client,
        selectedPackage.product_id,
        selectedPackage.package_id
    )
    const packageGross =
        (Number(selectedPackage.price) || 0) * packageQuantity
    const packageDiscount = getBulkEnrollmentDiscountAmount(
        selectedPackage.discount,
        packageQuantity,
        existingQuantity
    )
    const total = getBulkEnrollmentRowTotal(
        selectedPackage,
        packageQuantity,
        selectedAddOns,
        client
    )

    return (
        <div className="bulk-enrollment-client-packages">
            <div className="bulk-enrollment-package-row bulk-enrollment-package-row--selected bulk-enrollment-package-row--main">
                <div className="bulk-enrollment-package-row__product">
                    {selectedPackage.image_url ? (
                        <img
                            src={selectedPackage.image_url}
                            alt={selectedPackage.name}
                            className="bulk-enrollment-package-row__image"
                        />
                    ) : (
                        <div
                            className="bulk-enrollment-package-row__image bulk-enrollment-package-row__image--empty"
                            aria-hidden="true"
                        />
                    )}
                    <div className="bulk-enrollment-package-row__prices">
                        {packageDiscount > 0 && (
                            <p className="bulk-enrollment-package-row__discount">
                                {formatBulkEnrollmentDiscount(
                                    packageDiscount,
                                    selectedPackage.hidePrice
                                )}
                            </p>
                        )}
                        <p className="bulk-enrollment-package-row__line-total">
                            {formatBulkEnrollmentAmount(
                                packageGross - packageDiscount,
                                selectedPackage.hidePrice
                            )}
                        </p>
                    </div>
                </div>
                <BulkEnrollmentQuantityStepper
                    quantity={packageQuantity}
                    min={1}
                    label={selectedPackage.name}
                    disabled={readOnly}
                    onChange={(nextQuantity) =>
                        onQuantityChange(
                            String(
                                clampBulkEnrollmentPackageQuantity(
                                    nextQuantity
                                )
                            )
                        )
                    }
                />
            </div>
            {addOns.length > 0 && (
                <>
                    <div
                        className="bulk-enrollment-addons-divider"
                        aria-hidden="true"
                    />
                    <BulkEnrollmentAddOnGrid
                        addOns={addOns}
                        selectedAddOns={selectedAddOns}
                        client={client}
                        readOnly={readOnly}
                        onQuantityChange={(packageId, nextQuantity) =>
                            onAddOnsChange(
                                setBulkEnrollmentAddOnQuantity(
                                    selectedAddOns,
                                    packageId,
                                    nextQuantity
                                )
                            )
                        }
                    />
                </>
            )}
            <div className="bulk-enrollment-total-row">
                <BulkEnrollmentThumbnails
                    selectedPackage={selectedPackage}
                    selectedAddOns={selectedAddOns}
                />
                <p className="bulk-enrollment-total-row__amount">
                    {formatBulkEnrollmentAmount(
                        total,
                        selectedPackage.hidePrice
                    )}
                </p>
            </div>
        </div>
    )
}
